<script setup lang="ts">
import type { ButtonProps } from '#ui/types';
import lodashFindKey from 'lodash-es/findKey';
import type { PropType } from 'vue';

import type { SaleAssetBusEntity, SaleAssetBusListingItem, SaleAssetVideosEntity } from '~/apiClient';

// COMPOSABLES
const { tt } = useTypedI18n();
const { isMobile } = useResponsive();
const { swiperElement, activeIndex } = useSwiperNavigation();
const { getVideoSrc } = useVideoSrc();
const { attachSdpVideoTracker } = useTracking();

// PROPS
const { mainAsset } = defineProps({
    mainAsset: { type: Object as PropType<SaleAssetBusEntity | SaleAssetBusListingItem>, required: true },
});

// DATA
const items = mainAsset.videos;
const model = defineModel<boolean>({ default: false });
const videoElementRefs = ref<(HTMLVideoElement | null)[]>([]);
const videoTrackerCleanupFns = ref<VoidFunction[]>([]);
const buttonSize = computed<ButtonProps['size']>(() => (isMobile.value ? 'lg' : 'xl'));
const videos = computed<string[]>(() => {
    const { engine, gearbox, other } = items;

    return [...engine, ...gearbox, ...other];
});
const type = computed<TranslationKey | undefined>(() => {
    const videoKey = <keyof SaleAssetVideosEntity | undefined>(
        lodashFindKey(items, (srcList: string[]) => srcList.includes(videos.value[activeIndex.value]))
    );

    return videoKey === 'engine' ? 'asset.engine' : undefined;
});
const title = computed<string | undefined>(() => type.value && tt(type.value));
const { saleVideoAlt } = useA11y();

// METHODS
const setVideoRef = (el: Element | ComponentPublicInstance | null, index: number): void => {
    if (!(el instanceof HTMLVideoElement)) {
        videoTrackerCleanupFns.value?.[index]?.();
        videoElementRefs.value[index] = null;

        return;
    }

    if (videoElementRefs.value[index] === el) return;

    videoElementRefs.value[index] = el;
    videoTrackerCleanupFns.value[index] = attachSdpVideoTracker(el);
};

// WATCHERS
watch(activeIndex, (_, oldValue) => {
    videoElementRefs.value?.[oldValue]?.pause();
});

// LIFECYCLE
onUnmounted(() => {
    videoTrackerCleanupFns.value.forEach(cleanupFn => cleanupFn?.());
});
</script>

<template>
    <SdpLightboxWrapper v-model="model" index-i18n-key="sdp.videoPagination" :index="activeIndex" :total-items="videos.length" :title="title">
        <SdpSwiperNavigation
            class="w-[calc(100%-2.5rem)] max-w-7xl lg:w-[calc(100%-3.5rem)]"
            :swiper-element="swiperElement"
            :active-index="activeIndex"
            :size="buttonSize"
        />

        <swiper-container
            ref="swiperElement"
            class="aspect-video max-h-full max-w-full flex-1 rounded-xl lg:landscape:max-h-[calc(100%-5rem)]"
            :space-between="24"
            :breakpoints="{
                768: {
                    spaceBetween: 32,
                },
            }"
        >
            <swiper-slide v-for="(video, index) in videos" :key="video">
                <video
                    :ref="(el: Element | ComponentPublicInstance | null) => setVideoRef(el, index)"
                    controls
                    class="aspect-video h-full rounded-xl border object-cover"
                    :aria-label="saleVideoAlt(mainAsset, video)"
                    :src="getVideoSrc(video)"
                />
            </swiper-slide>
        </swiper-container>
    </SdpLightboxWrapper>
</template>
